import { html, css } from 'lit-element';
import { BaseElement } from './BaseElement';
import { faFastBackward, faStepBackward, faStepForward, faFastForward } from '@fortawesome/free-solid-svg-icons';

export const COLUMN_TYPE_NUMBER = 1;
export const COLUMN_TYPE_STRING = 2;
export const COLUMN_TYPE_BOOLEAN = 3;
export const COLUMN_TYPE_DATE = 4;
export const COLUMN_TYPE_DATETIME = 5;
export const COLUMN_TYPES = [
	COLUMN_TYPE_NUMBER,
	COLUMN_TYPE_STRING,
	COLUMN_TYPE_BOOLEAN,
	COLUMN_TYPE_DATE,
	COLUMN_TYPE_DATETIME
];

export const Column = (() => {
	const _name = Symbol();
	const _display = Symbol();
	const _type = Symbol();
	const _visible = Symbol();

	class Column {
		constructor(name, display, type, visible) {
			this.name = name;
			this.display = display;
			this.type = type;
			this.visible = visible;
		}
		get name() {
			return this[_name];
		}
		set name(v) {
			if(typeof v !== 'string')
				throw new TypeError('name must be a string.');
			this[_name] = v;
		}
		get display() {
			return this[_display];
		}
		set display(v) {
			if(v !== null && typeof v !== 'string')
				throw new TypeError('display must be a string.');
			this[_display] = v;
		}
		get type() {
			return this[_type];
		}
		set type(v) {
			if(COLUMN_TYPES.indexOf(v) === -1)
				throw new TypeError('Invalid column type.');
			this[_type] = v;
		}
		get visible() {
			return this[_visible];
		}
		set visible(v) {
			if(typeof v !== 'boolean')
				throw new TypeError('visible must be a boolean.');
			this[_visible] = v;
		}
		toJSON() {
			return {
				name: this.name,
				display: this.display,
				type: this.type,
				visible: this.visible
			};
		}
		static fromObject(obj) {
			if(obj === null || typeof obj !== 'object')
				throw new TypeError('obj must be an object.');
			return new this(
				obj.name || null,
				obj.display || null,
				obj.type || COLUMN_TYPE_STRING,
				obj.visible || true
			);
		}
	}

	return Column;
})();

export const Columns = (() => {
	const _items = Symbol();
	class Columns {
		constructor() {
			this[_items] = [];
		}
		//iterator protocol support
		*iterator() {
			for(let key in this[_items]) {
				let value = this[_items][key];
				yield value;
			}
		}
		//iterable protocol support
		[Symbol.iterator]() {
			return this.iterator();
		}
		get length() {
			return this[_items].length;
		}
		get(index) {
			return this[_items][index];
		}
		add(...columns) {
			columns.forEach((column, index) => {
				if(!(column instanceof Column))
					throw new TypeError(`argument at index ${index} must be an instance of Column.`);
			});
			
			this[_items].push(...columns);
		}
		toJSON() {
			return this[_items];
		}
		getState() {
			return JSON.parse(JSON.stringify(this));
		}
		setState(state) {
			if(!Array.isArray(state))
				throw new TypeError('state must be an array.');
			this[_items].length = 0;
			this.add(...state.map(v => Column.fromObject(v)));
		}
		static fromArray(arr) {
			if(!Array.isArray(arr))
				throw new TypeError('arr must be an array.');
			const columns = new this();
			
			arr.forEach(v => columns.add(Column.fromObject(v)));
			return columns;
		}
	}

	return Columns;
})();

export const Datatable = (() => {
	const DEFAULT_SORT_ASC = true;
	const DEFAULT_PAGE_SIZE = 25;
	const DEFAULT_PAGE_NUMBER = 0;
	const DEFAULT_TOTAL_RECORDS = 0;

	const _columns = Symbol();
	const _rows = Symbol();
	const _sortColumn = Symbol();
	const _sortASC = Symbol();
	const _pageSize = Symbol();
	const _pageNumber = Symbol();
	const _totalRecords = Symbol();
	const _stateId = Symbol();

	class Datatable extends BaseElement {
		constructor() {
			super();
			this[_rows] = [];
			this[_sortColumn] = null;
			this[_sortASC] = DEFAULT_SORT_ASC;
			this[_pageSize] = DEFAULT_PAGE_SIZE;
			this[_pageNumber] = DEFAULT_PAGE_NUMBER;
			this[_totalRecords] = DEFAULT_TOTAL_RECORDS;

			this.library.add(faFastBackward, faStepBackward, faStepForward, faFastForward);
		}
		static get styles() {
			return [
				...BaseElement.styles,
				css`
					table {
						width: 100%;
					}
					tr {
						cursor: pointer;
						transition: all 0.2s;
					}
					
					.sortAsc::after {
						content: ' ▲';
					}
					.sortDesc::after {
						content: ' ▼';
					}
					.page-bar {
						text-align: center;
						margin: 0.25em;
					}
					.button-bar {
						display: flex;
						justify-content: center;
						margin-top: 0.25em;
					}
					.button-bar button {
						margin-right: 0.25em;
					}
					.button-bar button:last {
						margin-right: 0;
					}
					
					button .icon {
						height: 1em;
						position: relative;
						top: 0.15em;
					}
				`
			];
		}
		get sortColumn() {
			return this[_sortColumn];
		}
		set sortColumn(v) {
			if(typeof v !== 'string')
				throw new TypeError('sortColumn must be a string.');
			this[_sortColumn] = v;
		}
		get sortAsc() {
			return this[_sortASC];
		}
		set sortAsc(v) {
			if(typeof v !== 'boolean')
				throw new TypeError('sortAsc must be a boolean.');
			this[_sortASC] = v;
		}
		get pageSize() {
			return this[_pageSize];
		}
		set pageSize(v) {
			if(!Number.isInteger(v))
				throw new TypeError('pageSize must be an integer.');
			if(v <= 0)
				throw new TypeError('pageSize must be one or greater.');
			this[_pageSize] = v;
			this.requestUpdate();
		}
		get pageNumber() {
			return this[_pageNumber];
		}
		set pageNumber(v) {
			if(!Number.isInteger(v))
				throw new TypeError('pageNumber must be an integer.');
			this[_pageNumber] = v;
			this.requestUpdate();
		}
		get totalRecords() {
			return this[_totalRecords];
		}
		set totalRecords(v) {
			if(!Number.isInteger(v))
				throw new TypeError('totalRecords must be an integer.');
			this[_totalRecords] = v;
			this.requestUpdate();
		}
		get columns() {
			return this[_columns];
		}
		set columns(v) {
			if(!(v instanceof Columns))
				throw new TypeError('columns must be an instance of Columns.');
			this[_columns] = v;
			if(this.columns.length > 0)
				this.sortColumn = this.columns.get(0).name;
		}
		get rows() {
			return this[_rows];
		}
		set rows(v) {
			if(!Array.isArray(v))
				throw new TypeError('rows must be an array.');
			this[_rows] = v;
			this.requestUpdate();
		}
		get stateId() {
			return this[_stateId];
		}
		set stateId(v) {
			if(v !== null && typeof v !== 'string')
				throw new TypeError('stateId must be a string.');
			this[_stateId] = v;
		}
		headerClickHandler(name) {
			if(typeof name !== 'string')
				throw new TypeError('name must be a string.');
			if(this.sortColumn !== name)
				this.sortColumn = name;
			else this.sortAsc = !this.sortAsc;
			
			this.refreshData();
		}
		refreshData() {
			this.emit('refresh-data', {
				sortColumn: this.sortColumn,
				sortAsc: this.sortAsc,
				pageNumber: this.pageNumber
			});
		}
		selectClickHandler(pageNumber) {
			if(!Number.isInteger(pageNumber))
				throw new TypeError('pageNumber must be an integer.');
			
			this.pageNumber = pageNumber;
		}
		get pageCount() {
			return Math.ceil(this.totalRecords / this.pageSize);
		}
		firstPage() {
			this.pageNumber = 0;
			this.refreshData();
		}
		prevPage() {
			this.pageNumber = Math.max(this.pageNumber - 1, 0);
			this.refreshData();
		}
		nextPage() {
			this.pageNumber = Math.min(this.pageNumber + 1, Math.max(this.pageCount - 1, 0));
			this.refreshData();
		}
		lastPage() {
			this.pageNumber = Math.max(this.pageCount - 1, 0);
			this.refreshData();
		}
		page(pageNumber) {
			if(!Number.isInteger(pageNumber))
				throw new TypeError('pageNumber must be an integer.');
			this.pageNumber = pageNumber;
			this.refreshData();
		}
		toJSON() {
			return {
				totalRecords: this.totalRecords,
				pageSize: this.pageSize,
				pageNumber: this.pageNumber,
				sortColumn: this.sortColumn,
				sortAsc: this.sortAsc,
				rows: this.rows
			};
		}
		firstUpdated(changedProperties) {
			super.firstUpdated(changedProperties);
			if(!this.stateId)
				return;
			const state = this.state.get(this.stateId);
			if(!state) return;
			
			this.totalRecords = state.totalRecords;
			this.pageSize = state.pageSize;
			this.pageNumber = state.pageNumber;
			this.sortAsc = state.sortAsc;
			this.sortColumn = state.sortColumn;
			this.rows = state.rows;
		}
		connectedCallback() {
			super.connectedCallback();
			this.emit('get-state', { callback: state => this.state = state });
		}
		disconnectedCallback() {
			super.disconnectedCallback();
			
			this.state = null;
		}
		render() {
			if(this.stateId && this.hasUpdated)
				this.state.set(this.stateId, JSON.parse(JSON.stringify(this)), true);
			
			const sortClass = this.sortAsc ? 'sortAsc' : 'sortDesc';
			const pages = Math.ceil(this.totalRecords / this.pageSize);
			return html`
				<div class="page-bar">
					<!-- Page Selection -->
					<select @change="${e => this.page(parseInt(e.target.value, 10))}">
						${[...Array(pages)].map((page, index) => html`
							<option
								value="${index}"
								.selected="${this.pageNumber === index}"
							>Page ${index+1} of ${pages} (${this.totalRecords} total records)</option>
						`)}
					</select>
					<div class="button-bar">
						<button @click=${() => this.firstPage()}>
							<i class="icon fas fa-fast-backward"></i> First
						</button>
						<button @click=${() => this.prevPage()}>
							<i class="icon fas fa-step-backward"></i> Prev
						</button>
						<button @click=${() => this.nextPage()}>
							<i class="icon fas fa-step-forward"></i> Next
						</button>
						<button @click=${() => this.lastPage()}>
							<i class="icon fas fa-fast-forward"></i> Last
						</button>
					</div>
				</div>
				<table class="data-table">
					<!-- Table header -->
					<thead>
						<tr>
						${[...this.columns].map((column, index) => html`
							<th class="${column.name === this.sortColumn ? sortClass : ''}" @click="${() => this.headerClickHandler(column.name)}">${column.display || column.name}</th>
						`)}
						</tr>
					</thead>
					<!-- Table body -->
					<tbody>
						${this.rows.map((row, index) => html`
							<tr tabindex="0" @click="${() => this.emit('row-click', row)}" class="${index%2 === 1 ? 'odd' : 'even'}">
								${[...this.columns].map(column => html`
									<td>${row[column.name]}</td>
								`)}
							</tr>
						`)}
					</tbody>
				</table>
			`;
		}
	}

	return Datatable;
})();


customElements.define('pp-datatable', Datatable);